import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
  Toast,
  Toggle,
} from '@/components/ui';
import { formatDateTime, formatService } from '@/lib/format';

interface Bot {
  id: string;
  name: string;
  code: string;
  status: string;
  bot_group_id: string | null;
  max_concurrent: number;
  config: BotConfig;
  created_at: string;
  updated_at: string;
}

interface OfferingRow {
  key: string;
  label: string;
  summary: string;
  required_fields: string[];
  max_turns: number;
}

interface BotConfig {
  /** "BOT" screens and transfers; "AGENT" runs the full qualification. */
  mode?: string;
  company?: {
    name?: string;
    brand?: string;
    agent_name?: string;
    greeting?: string;
    closing?: string;
    ai_disclosure?: string;
    tone?: string;
    language?: string;
  };
  voice?: { voice_id?: string; speed?: number; barge_in?: boolean };
  prompt?: { persona?: string; system_extra?: string; rules?: string[] };
  qualification?: {
    required_fields?: string[];
    min_score?: number;
    max_questions?: number;
    /** Screener mode only. False asks nothing but ownership. */
    ask_service?: boolean;
  };
  transfer?: {
    mode?: string;
    in_group?: string;
    verifier?: string;
    direct_agent?: string;
    timeout_sec?: number;
    /** BLIND leaves the call; WARM keeps this platform in the media path. */
    handoff?: string;
    /** What the customer hears while the closer is being found. */
    connect_audio?: string;
    fallback?: string[];
    max_attempts?: number;
    /** One line played to the closer alone, before the customer is bridged. */
    whisper_enabled?: boolean;
    whisper_template?: string;
    whisper_max_sec?: number;
    /** Ask the dialer whether a closer is free before offering a transfer. */
    require_available_closer?: boolean;
    on_no_closer?: string;
  };
  compliance?: {
    dnc_enabled?: boolean;
    end_on_not_interested?: boolean;
    disclosure_required?: boolean;
  };
  /**
   * What a call may cost before the bot gives up on it. Every one of these
   * is a carrier minute — the bot reaches a machine or a dead line far more
   * often than a person, and what decides the bill is how fast it works that
   * out.
   */
  limits?: {
    silence_prompt_sec?: number;
    silence_hangup_sec?: number;
    max_call_seconds?: number;
    max_repeats?: number;
    hangup_on_dead_air?: boolean;
    dead_line_after_sec?: number;
  };
  /**
   * Answering-machine detection from the audio, before anything is said.
   * Separate from the transcript detector, which is always on and free.
   */
  amd?: {
    enabled?: boolean;
    beep_detection?: boolean;
    cadence_detection?: boolean;
    sensitivity?: string;
    voicemail_drop?: boolean;
    voicemail_message?: string;
    voicemail_wait_for_beep?: boolean;
    voicemail_beep_wait_sec?: number;
  };
}

/** The keys of BotConfig whose value is a nested object. */
type SectionKey = {
  [K in keyof BotConfig]-?: NonNullable<BotConfig[K]> extends object ? K : never;
}[keyof BotConfig];

interface ServiceRow {
  key: string;
  name: string;
}

interface VoiceRow {
  voice_id: string;
  name: string;
  gender?: string;
  accent?: string;
  description?: string;
}

/** "Rachel - female, american" - enough to choose by without an audition. */
function describeVoice(voice: VoiceRow): string {
  const traits = [voice.gender, voice.accent, voice.description]
    .map((t) => (t ?? '').trim())
    .filter(Boolean);
  return traits.length ? `${voice.name} \u2014 ${traits.join(', ')}` : voice.name;
}

const TABS = [
  'Company',
  'Voice',
  'Qualification',
  'Transfer',
  'Efficiency',
  'Compliance',
  'Advanced',
] as const;
type Tab = (typeof TABS)[number];

export default function BotDetail() {
  const { botId } = useParams<{ botId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('Company');
  const [draft, setDraft] = useState<BotConfig>({});
  const [name, setName] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const { data: bot, isPending, error, refetch } = useQuery({
    queryKey: ['bot', botId],
    queryFn: () => api.get<Bot>(`/bots/${botId}`),
    enabled: Boolean(botId),
  });

  const { data: services } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ data: ServiceRow[] }>('/services'),
  });

  // The provider's own catalogue, so a cloned voice is selectable too. A
  // failure here is not fatal: the field falls back to accepting an id.
  // Which products this client is entitled to. The server refuses a mode that
  // is not in this list, so hiding it here is a courtesy rather than the check.
  const { data: offerings } = useQuery({
    queryKey: ['offerings'],
    queryFn: () => api.get<{ data: OfferingRow[] }>('/offerings'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Keyed and fetched by bot: a bot may use its own TTS provider, and voices
  // from a different account would be ids the synthesis cannot resolve.
  const { data: voices, isPending: voicesPending } = useQuery({
    queryKey: ['voices', botId],
    queryFn: () =>
      api.get<{ data: VoiceRow[]; provider: string | null; mock?: boolean }>(
        `/voices?bot_id=${botId}`,
      ),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (bot) {
      setDraft(bot.config ?? {});
      setName(bot.name);
      setStatus(bot.status);
    }
  }, [bot]);

  const save = useMutation({
    mutationFn: () => api.patch(`/bots/${botId}`, { name, status, config: draft }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot', botId] });
      queryClient.invalidateQueries({ queryKey: ['bots'] });
      setToast({ message: 'Bot saved', tone: 'good' });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not save'),
        tone: 'bad',
      }),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const readOnly = !can('bots:update');

  const offeringsList = offerings?.data ?? [];
  // AGENT, not BOT, when nothing is stored: the same default the engine uses.
  // Guessing the short flow here would show a screener on a bot that is about
  // to run the long one.
  const mode = (draft.mode ?? 'AGENT').toUpperCase();
  const activeOffering = offeringsList.find((o) => o.key === mode) ?? null;

  const voicesList = voices?.data ?? [];
  const selectedVoice = draft.voice?.voice_id ?? '';
  const byGender = (want: string) =>
    voicesList.filter((v) => (v.gender ?? '').trim().toLowerCase() === want);
  const femaleVoices = byGender('female');
  const maleVoices = byGender('male');
  const otherVoices = voicesList.filter(
    (v) => !['female', 'male'].includes((v.gender ?? '').trim().toLowerCase()),
  );
  // A configured id the provider no longer lists. Kept as an option so the
  // picker shows the truth instead of silently reading "Platform default".
  const unlistedVoice =
    selectedVoice && !voicesList.some((v) => v.voice_id === selectedVoice)
      ? selectedVoice
      : '';

  // Only the nested sections, never `mode` -- that is a bare string, and
  // spreading it would silently produce a config the engine cannot read.
  const setSection = <K extends SectionKey>(section: K, patch: Partial<BotConfig[K]>) => {
    setDraft((prev) => ({
      ...prev,
      [section]: { ...(prev[section] ?? {}), ...patch },
    }));
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={bot.name}
        description={`Code ${bot.code} · updated ${formatDateTime(bot.updated_at)}`}
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate('/bots')}>
              Back
            </Button>
            {!readOnly && (
              <Button loading={save.isPending} onClick={() => save.mutate()}>
                Save changes
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap gap-1 border-b border-line">
        {TABS.map((item) => (
          <button
            key={item}
            onClick={() => setTab(item)}
            className={
              tab === item
                ? 'border-b-2 border-brand-600 px-3 py-2 text-sm font-medium text-brand-700 dark:text-brand-400'
                : 'border-b-2 border-transparent px-3 py-2 text-sm text-ink-500 hover:text-ink-800 dark:hover:text-ink-200'
            }
          >
            {item}
          </button>
        ))}
      </div>

      {tab === 'Company' && (
        <div className="space-y-4">
        <Card
          title="What this bot does"
          description="The single biggest choice on this page: how far the conversation goes before a human takes over."
        >
          <div className="space-y-2">
            {offeringsList.length === 0 && (
              <p className="text-xs text-ink-500 dark:text-ink-400">
                Loading the products available on this account&hellip;
              </p>
            )}
            {offeringsList.map((offering) => (
              <label
                key={offering.key}
                className={`flex cursor-pointer gap-3 rounded-md border p-3 ${
                  mode === offering.key
                    ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-950/30'
                    : 'border-line'
                } ${readOnly ? 'cursor-not-allowed opacity-70' : ''}`}
              >
                <input
                  type="radio"
                  name="bot-mode"
                  className="mt-1"
                  value={offering.key}
                  checked={mode === offering.key}
                  onChange={() => setDraft((prev) => ({ ...prev, mode: offering.key }))}
                  disabled={readOnly}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{offering.label}</span>
                  <span className="block text-xs text-ink-600 dark:text-ink-300">
                    {offering.summary}
                  </span>
                  <span className="mt-1 block text-xs text-ink-500 dark:text-ink-400">
                    Asks for: {offering.required_fields.join(', ')} &middot; at most{' '}
                    {offering.max_turns} turns
                  </span>
                </span>
              </label>
            ))}
          </div>

          {activeOffering?.key === 'BOT' && (
            <div className="mt-4">
              <Toggle
                label="Ask what service they need"
                hint="Off asks nothing but whether they own the home, then transfers. The closer is going to ask anyway, and every extra question is a chance to be hung up on."
                checked={draft.qualification?.ask_service ?? true}
                onChange={(v) => setSection('qualification', { ask_service: v })}
                disabled={readOnly}
              />
            </div>
          )}

          {activeOffering?.key === 'BOT' && (
            <div className="mt-3 rounded-md bg-sunken p-3 text-xs text-ink-600 dark:text-ink-300">
              In this mode the Qualification tab is not used.{' '}
              {draft.qualification?.ask_service === false
                ? 'The bot asks only whether they own the home, then transfers.'
                : 'The bot asks whether they own the home and roughly what they need, then transfers.'}{' '}
              It will not ask for a ZIP code, measurements, counts, budget or
              timing, whatever else is configured.
            </div>
          )}
        </Card>

        <Card
          title="Company identity"
          description="Never hard-coded — the bot introduces itself with whatever you set here."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Bot name">
              <Input value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} />
            </Field>
            <Field label="Status">
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={readOnly}
              >
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
                <option value="DRAFT">Draft</option>
              </Select>
            </Field>
            <Field
              label="Agent name"
              hint="What the bot calls itself. A call that opens with a company name alone reads as a recording."
            >
              <Input
                value={draft.company?.agent_name ?? ''}
                onChange={(e) => setSection('company', { agent_name: e.target.value })}
                placeholder="Sarah"
                disabled={readOnly}
              />
            </Field>
            <Field label="Company name" hint="Spoken to the customer.">
              <Input
                value={draft.company?.name ?? ''}
                onChange={(e) => setSection('company', { name: e.target.value })}
                placeholder="HomeSolvePro"
                disabled={readOnly}
              />
            </Field>
            <Field label="Brand" hint="Optional, when it differs from the legal name.">
              <Input
                value={draft.company?.brand ?? ''}
                onChange={(e) => setSection('company', { brand: e.target.value })}
                disabled={readOnly}
              />
            </Field>
          </div>

          <div className="mt-4 space-y-4">
            <Field
              label="Greeting"
              hint="{{agent_name}}, {{company}} and {{first_name}} are substituted at call time."
            >
              <Textarea
                rows={2}
                value={draft.company?.greeting ?? ''}
                onChange={(e) => setSection('company', { greeting: e.target.value })}
                disabled={readOnly}
              />
            </Field>
            <Field
              label="AI disclosure"
              hint="Spoken whenever the customer asks whether they are talking to a person. Required by policy — the bot must never claim to be human."
            >
              <Input
                value={draft.company?.ai_disclosure ?? ''}
                onChange={(e) => setSection('company', { ai_disclosure: e.target.value })}
                disabled={readOnly}
              />
            </Field>
            <Field label="Closing">
              <Input
                value={draft.company?.closing ?? ''}
                onChange={(e) => setSection('company', { closing: e.target.value })}
                disabled={readOnly}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Tone">
                <Input
                  value={draft.company?.tone ?? ''}
                  onChange={(e) => setSection('company', { tone: e.target.value })}
                  placeholder="warm, efficient, plain-spoken"
                  disabled={readOnly}
                />
              </Field>
              <Field label="Language">
                <Input
                  value={draft.company?.language ?? ''}
                  onChange={(e) => setSection('company', { language: e.target.value })}
                  placeholder="en-US"
                  disabled={readOnly}
                />
              </Field>
            </div>
          </div>
        </Card>
        </div>
      )}

      {tab === 'Qualification' && (
        <div className="space-y-4">
          <Card
            title="Qualification"
            description="What the bot must learn before a lead is worth a human's time."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Minimum lead score"
                hint="0–100. Both this AND every required field must be satisfied to transfer."
              >
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={draft.qualification?.min_score ?? 60}
                  onChange={(e) =>
                    setSection('qualification', { min_score: Number(e.target.value) })
                  }
                  disabled={readOnly}
                />
              </Field>
              <Field
                label="Max questions"
                hint="Hard stop, so a confused conversation cannot run up cost."
              >
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={draft.qualification?.max_questions ?? 8}
                  onChange={(e) =>
                    setSection('qualification', { max_questions: Number(e.target.value) })
                  }
                  disabled={readOnly}
                />
              </Field>
            </div>

            <Field
              label="Required fields"
              hint="Comma separated. Each identified service can add its own on top."
            >
              <Input
                value={(draft.qualification?.required_fields ?? []).join(', ')}
                onChange={(e) =>
                  setSection('qualification', {
                    required_fields: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="service, homeowner, postal_code, project_details"
                disabled={readOnly}
              />
            </Field>
          </Card>

          <Card
            title="Services this bot understands"
            description="The whole catalog, always. One engine, every vertical."
          >
            <div className="flex flex-wrap gap-1.5">
              {(services?.data ?? []).map((service) => (
                <Badge key={service.key} tone="blue">
                  {formatService(service.key)}
                </Badge>
              ))}
            </div>
            <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
              The catalog is managed by the platform. A customer can raise several of these
              in one call and the bot carries all of them forward.
            </p>
          </Card>
        </div>
      )}

      {tab === 'Transfer' && (
        <Card
          title="Where a qualified call goes"
          description="Configured per Vicidial server, not here."
        >
          <p className="text-sm text-ink-600 dark:text-ink-300">
            A transfer destination belongs to the dialer, not the bot: the same bot can
            serve several Vicidial servers, and each hands back to its own closers. It used
            to be settable in both places, with a priority chain deciding between them, so
            the only way to know where a call would actually go was to read the code.
          </p>
          <p className="mt-3 text-sm text-ink-600 dark:text-ink-300">
            Open <strong>Vicidial servers</strong>, pick the server, and set{' '}
            <strong>Transfer method</strong> there — dialplan extension, in-group, or
            closer DID — along with the field that method needs.
          </p>
          <div className="mt-4">
            <Button variant="secondary" onClick={() => navigate('/vicidial-servers')}>
              Go to Vicidial servers
            </Button>
          </div>

          <div className="mt-4 rounded-md bg-sunken p-3 text-xs text-ink-600 dark:text-ink-300">
            The dialplan extension is the best path where it applies: handing the channel
            to Asterisk both dials the closer and takes the bot out of the call, rather
            than this platform having to remember to do the second part.
          </div>

          {/* The shape of the hand-over, as opposed to its destination.
              Separate from the method above on purpose: the method says WHERE
              a qualified call goes, this says whether this platform stays in
              the call after it gets there — which is what somebody is
              describing when they say the bot is still in a three-way. */}
          <div className="mt-6 border-t border-line pt-5">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">
              How the hand-over is done
            </h3>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field
                label="Hand-over"
                hint="Blind needs a Transfer extension on the Vicidial server — that dialplan is what does the dialling. Without one the transfer still happens, warm, and says so in the log."
              >
                <Select
                  value={draft.transfer?.handoff ?? 'WARM'}
                  disabled={readOnly}
                  onChange={(e) => setSection('transfer', { handoff: e.target.value })}
                >
                  <option value="WARM">
                    Warm — this platform dials the closer and bridges
                  </option>
                  <option value="BLIND">
                    Blind — hand the call to the dialplan and leave
                  </option>
                </Select>
              </Field>

              <Field
                label="While the closer is being found"
                hint="Only applies to a warm hand-over. On a blind one the dialplan gives ringback itself."
              >
                <Select
                  value={draft.transfer?.connect_audio ?? 'RINGING'}
                  disabled={readOnly}
                  onChange={(e) =>
                    setSection('transfer', { connect_audio: e.target.value })
                  }
                >
                  <option value="RINGING">Ringing</option>
                  <option value="MUSIC">Music on hold</option>
                  <option value="SILENCE">Nothing</option>
                </Select>
              </Field>
            </div>

            <div className="mt-3 rounded-md bg-sunken p-3 text-xs leading-relaxed text-ink-600 dark:text-ink-300">
              <p>
                <strong>Warm</strong> dials the closer from here, holds their
                leg and bridges the customer to it. The bot stops speaking and
                stops listening, but the customer&apos;s channel never leaves
                this platform &mdash; so it is in the media path until somebody
                hangs up, the channel stays listed as ours, and it is what
                people mean when they say the call sounds like a three-way. It
                is also the only way a whisper can be played.
              </p>
              <p className="mt-2">
                <strong>Blind</strong> hands the channel back to the dialplan
                and leaves. Asterisk dials the closer and bridges; nothing is
                held here. What you give up is the whisper and the fallback
                chain: once the channel has gone, this platform cannot tell
                whether a human ever picked up, so a blind transfer is counted
                as connected at the moment it is handed over.
              </p>
            </div>
          </div>

          <div className="mt-6 border-t border-line pt-5">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">
              Check somebody is free first
            </h3>
            <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
              Today the bot qualifies a lead, offers the transfer, and finds
              out. When nobody is free the fallback chain runs &mdash; retry,
              apologise, hang up &mdash; and a customer who said yes has had it
              taken away again. It is the most expensive possible way to learn
              something the dialer knew before the question was asked.
            </p>

            <div className="mt-4 space-y-3">
              <Toggle
                label="Ask the dialer before offering a transfer"
                hint="Needs Remote Agents set up on the Vicidial users screen. If the dialer cannot be reached the transfer goes ahead as before — an unreachable dialer must never stop every transfer."
                checked={draft.transfer?.require_available_closer ?? false}
                onChange={(v) =>
                  setSection('transfer', { require_available_closer: v })
                }
                disabled={readOnly}
              />

              {draft.transfer?.require_available_closer && (
                <Field
                  label="When nobody is free"
                  hint="A callback keeps the lead. Ending the call spends it."
                >
                  <Select
                    value={draft.transfer?.on_no_closer ?? 'CALLBACK'}
                    disabled={readOnly}
                    onChange={(e) =>
                      setSection('transfer', { on_no_closer: e.target.value })
                    }
                  >
                    <option value="CALLBACK">
                      Schedule a callback and say so
                    </option>
                    <option value="END_CALL">Apologise and end the call</option>
                  </Select>
                </Field>
              )}
            </div>
          </div>

          <div className="mt-6 border-t border-line pt-5">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">
              Tell the closer who they are about to speak to
            </h3>
            <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
              A warm transfer today is not warm. The closer's phone rings, they
              answer, and a stranger is already on the line — so they open by
              asking the three questions the bot has just spent a minute asking.
              That is the moment a qualified lead works out it is talking to a
              call centre.
            </p>
            <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">
              This plays one sentence to the closer alone, after they pick up and
              before the customer is put through to them. Everything in it is
              already known by then.
            </p>

            <div className="mt-4 space-y-3">
              <Toggle
                label="Whisper a summary to the closer"
                hint="Off by default: every word of it is a word of silence for a customer who has just agreed to be transferred."
                checked={draft.transfer?.whisper_enabled ?? false}
                onChange={(v) => setSection('transfer', { whisper_enabled: v })}
                disabled={readOnly}
              />

              {draft.transfer?.whisper_enabled && (
                <div className="space-y-3 rounded-md border border-line p-3">
                  <Field
                    label="What to say"
                    hint="Leave empty for the summary the bot already builds. Placeholders: {name}, {phone}, {service}, {score}, {campaign}, {summary}."
                  >
                    <Input
                      disabled={readOnly}
                      value={draft.transfer?.whisper_template ?? ''}
                      placeholder="{name}, {service}, scored {score}."
                      onChange={(e) =>
                        setSection('transfer', { whisper_template: e.target.value })
                      }
                    />
                  </Field>

                  <Field
                    label="At most (seconds)"
                    hint="Anything longer is cut, on a word, before it is spoken. The customer is hearing silence for all of it."
                  >
                    <Input
                      type="number"
                      min={2}
                      max={20}
                      disabled={readOnly}
                      value={draft.transfer?.whisper_max_sec ?? 8}
                      onChange={(e) =>
                        setSection('transfer', {
                          whisper_max_sec: e.target.value ? Number(e.target.value) : 8,
                        })
                      }
                    />
                  </Field>

                  <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                    <strong>This needs the Closer DID transfer method.</strong> On
                    that path Glee dials the closer and bridges the customer to
                    them, so there is a moment when only the closer can hear. An
                    in-group transfer is dialled and bridged by Vicidial itself and
                    this platform never holds the agent's line — the setting will
                    stay on and say so in the log, but there is nowhere to play it.
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {tab === 'Efficiency' && (
        <Card
          title="Call limits"
          description="What a call is allowed to cost you before the bot gives up on it. Every one of these is a carrier minute."
        >
          <div className="rounded-md bg-sunken p-3 text-xs text-ink-600 dark:text-ink-300">
            The bot is talking to a machine or a dead line far more often than to
            a person — that is the nature of outbound dialling, not a fault. What
            decides the bill is how quickly it works that out and hangs up. The
            defaults are deliberately not the safest possible values; they are
            the ones that stop paying for silence.
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field
              label="Nudge after (seconds)"
              hint="Silence this long and the bot asks once whether anyone is there."
            >
              <Input
                type="number"
                min={2}
                max={30}
                disabled={readOnly}
                value={draft.limits?.silence_prompt_sec ?? ''}
                placeholder="8"
                onChange={(e) =>
                  setSection('limits', {
                    silence_prompt_sec: e.target.value ? Number(e.target.value) : 0,
                  })
                }
              />
            </Field>

            <Field
              label="Then hang up after (seconds)"
              hint="How long the nudge is given to be answered."
            >
              <Input
                type="number"
                min={2}
                max={30}
                disabled={readOnly}
                value={draft.limits?.silence_hangup_sec ?? ''}
                placeholder="10"
                onChange={(e) =>
                  setSection('limits', {
                    silence_hangup_sec: e.target.value ? Number(e.target.value) : 0,
                  })
                }
              />
            </Field>

            <Field
              label="Maximum call length (seconds)"
              hint="A hard ceiling, whatever the conversation is doing. Empty uses the mode's own."
            >
              <Input
                type="number"
                min={30}
                max={3600}
                disabled={readOnly}
                value={draft.limits?.max_call_seconds ?? ''}
                placeholder="mode default"
                onChange={(e) =>
                  setSection('limits', {
                    max_call_seconds: e.target.value ? Number(e.target.value) : 0,
                  })
                }
              />
            </Field>

            <Field
              label="Ask the same thing at most (times)"
              hint="Then the bot says goodbye and hangs up rather than asking again."
            >
              <Input
                type="number"
                min={1}
                max={5}
                disabled={readOnly}
                value={draft.limits?.max_repeats ?? ''}
                placeholder="3"
                onChange={(e) =>
                  setSection('limits', {
                    max_repeats: e.target.value ? Number(e.target.value) : 0,
                  })
                }
              />
            </Field>
          </div>

          <div className="mt-4">
            <Field
              label="Decide a dead line after (seconds)"
              hint="A line carrying no audio at all is not somebody thinking — it is a dead number, a channel that answered into nothing, or a trunk that connected and never passed audio. Measured on real traffic, half of all carrier time went on these, each waiting the full silence budget. 0 waits the whole budget, as before. Only applies when audio is plainly arriving: a media path delivering nothing is a different fault and keeps the full budget."
            >
              <Input
                type="number"
                min={0}
                max={30}
                step={0.5}
                disabled={readOnly}
                value={draft.limits?.dead_line_after_sec ?? 4}
                onChange={(e) =>
                  setSection('limits', {
                    dead_line_after_sec: Number(e.target.value),
                  })
                }
              />
            </Field>
          </div>

          <div className="mt-4">
            <Toggle
              label="Hang up promptly on a dead line"
              hint="Nobody answered the greeting and nobody answered the nudge. End it in a few seconds rather than waiting the full silence budget a second time."
              checked={draft.limits?.hangup_on_dead_air ?? true}
              onChange={(v) => setSection('limits', { hangup_on_dead_air: v })}
              disabled={readOnly}
            />
          </div>

          <div className="mt-4 rounded-md border border-line p-3 text-xs text-ink-600 dark:text-ink-300">
            <p className="font-medium text-ink-900 dark:text-ink-50">
              Worst case for one dead call, as configured:
            </p>
            <p className="mt-1 tabular-nums">
              {(draft.limits?.silence_prompt_sec || 8)} s of silence
              {' + '}
              {draft.limits?.hangup_on_dead_air ?? true
                ? '4'
                : (draft.limits?.silence_hangup_sec || 10)}{' '}
              s after the nudge ={' '}
              <strong>
                about{' '}
                {(draft.limits?.silence_prompt_sec || 8) +
                  (draft.limits?.hangup_on_dead_air ?? true
                    ? 4
                    : draft.limits?.silence_hangup_sec || 10)}{' '}
                seconds
              </strong>
              , against a hard ceiling of{' '}
              {draft.limits?.max_call_seconds || 'the mode default'}
              {draft.limits?.max_call_seconds ? ' seconds' : ''}.
            </p>
            <p className="mt-2">
              Leave a box empty to use the engine's own value. Zero is not
              &ldquo;no limit&rdquo; — it means the default, because a bot with
              no silence budget would hang up on everybody the moment they
              stopped speaking.
            </p>
          </div>

          <div className="mt-6 border-t border-line pt-5">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">
              Answering-machine detection
            </h3>
            <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
              The bot always reads the transcript for what a machine says —
              &ldquo;please leave a message after the tone&rdquo; — and that
              costs nothing, because the transcript arrives anyway. It is also
              late: it cannot decide until the machine has finished a sentence
              and speech recognition has sent it back, which is eight to fifteen
              seconds of carrier time on every machine.
            </p>
            <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">
              This listens to the audio instead and usually decides in two or
              three. Leave it off if your dialer's own AMD is enabled — two
              detectors both allowed to hang up is twice the chance of hanging
              up on somebody real.
            </p>

            <div className="mt-4 space-y-3">
              <Toggle
                label="Detect answering machines from the audio"
                hint="For accounts where the dialer's own AMD is turned off."
                checked={draft.amd?.enabled ?? false}
                onChange={(v) => setSection('amd', { enabled: v })}
                disabled={readOnly}
              />

              {draft.amd?.enabled && (
                <div className="space-y-3 rounded-md border border-line p-3">
                  <Toggle
                    label="Voicemail tone"
                    hint="A beep is not a hint that this might be a machine — it is the machine saying so. Nothing in speech is a steady tone held for a fifth of a second, so this has essentially no chance of being wrong."
                    checked={draft.amd?.beep_detection ?? true}
                    onChange={(v) => setSection('amd', { beep_detection: v })}
                    disabled={readOnly}
                  />
                  <Toggle
                    label="Talks without stopping"
                    hint="A person says a word or two and waits to hear who this is. A recording does not stop, because it is not waiting for anybody. This is the fast one, and the one with a real cost of being wrong."
                    checked={draft.amd?.cadence_detection ?? true}
                    onChange={(v) => setSection('amd', { cadence_detection: v })}
                    disabled={readOnly}
                  />

                  {(draft.amd?.cadence_detection ?? true) && (
                    <Field
                      label="How long is too long"
                      hint="How much unbroken speech counts as a recording. The right answer is a property of your list, not of the platform."
                    >
                      <Select
                        value={draft.amd?.sensitivity ?? 'balanced'}
                        disabled={readOnly}
                        onChange={(e) =>
                          setSection('amd', { sensitivity: e.target.value })
                        }
                      >
                        <option value="cautious">
                          Cautious — 4.5 s. Business lists, where people answer with
                          a whole sentence.
                        </option>
                        <option value="balanced">
                          Balanced — 3.5 s. The default.
                        </option>
                        <option value="aggressive">
                          Aggressive — 2.5 s. Residential lists, where people say
                          &ldquo;hello?&rdquo; and stop.
                        </option>
                      </Select>
                    </Field>
                  )}

                  <p className="rounded-md bg-sunken p-3 text-xs text-ink-600 dark:text-ink-300">
                    This cannot decide before the call is answered, and your
                    dialer's AMD can. By the time audio reaches this platform
                    Asterisk has answered and the carrier is already counting —
                    so turning the dialer's AMD off and this on trades a detector
                    that decides at zero seconds for one that decides at two or
                    three.
                  </p>

                  <div className="border-t border-line pt-3">
                    <Toggle
                      label="Leave a message instead of hanging up"
                      hint="A machine that is hung up on produces nothing. One that is left a message is a number that has heard the offer and can call back."
                      checked={draft.amd?.voicemail_drop ?? false}
                      onChange={(v) => setSection('amd', { voicemail_drop: v })}
                      disabled={readOnly}
                    />

                    {draft.amd?.voicemail_drop && (
                      <div className="mt-3 space-y-3">
                        <Field
                          label="What to say"
                          hint="Leave empty and nothing is left at all — a message that says nothing is worse than none, because they hear a hang-up-shaped silence and call back annoyed."
                        >
                          <Textarea
                            rows={3}
                            disabled={readOnly}
                            value={draft.amd?.voicemail_message ?? ''}
                            placeholder="Hi, this is Sarah from Home Solve Pro. Give us a call back on…"
                            onChange={(e) =>
                              setSection('amd', { voicemail_message: e.target.value })
                            }
                          />
                        </Field>

                        <Toggle
                          label="Wait for the beep"
                          hint="Only the tone means the recording has started. Recognised by its greeting, the machine is still talking, and a message played over that is half a message."
                          checked={draft.amd?.voicemail_wait_for_beep ?? true}
                          onChange={(v) =>
                            setSection('amd', { voicemail_wait_for_beep: v })
                          }
                          disabled={readOnly}
                        />

                        {(draft.amd?.voicemail_wait_for_beep ?? true) && (
                          <Field
                            label="Give up waiting after (seconds)"
                            hint="Then leave it anyway. Plenty of systems use a tone this does not recognise, and a message that might be truncated beats certainly nothing."
                          >
                            <Input
                              type="number"
                              min={0}
                              max={60}
                              disabled={readOnly}
                              value={draft.amd?.voicemail_beep_wait_sec ?? 25}
                              onChange={(e) =>
                                setSection('amd', {
                                  voicemail_beep_wait_sec: Number(e.target.value),
                                })
                              }
                            />
                          </Field>
                        )}

                        <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                          <strong>Check this is allowed for your campaign.</strong>{' '}
                          An unattended prerecorded message to a residential line
                          is regulated in most of the places this platform is
                          used. This is a decision about the account's compliance
                          posture, not a tuning knob.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {tab === 'Compliance' && (
        <Card title="Compliance" description="These behaviours are enforced, not suggested.">
          <div className="space-y-3">
            <Toggle
              label="Honour do-not-call requests"
              hint="Suppress locally, mirror to Vicidial, end the call. Never transfer."
              checked={draft.compliance?.dnc_enabled ?? true}
              onChange={(v) => setSection('compliance', { dnc_enabled: v })}
              disabled={readOnly}
            />
            <Toggle
              label="End the call on a refusal"
              hint="Accept the first no. One acknowledgement, then hang up."
              checked={draft.compliance?.end_on_not_interested ?? true}
              onChange={(v) => setSection('compliance', { end_on_not_interested: v })}
              disabled={readOnly}
            />
            <Toggle
              label="Require AI disclosure"
              hint="Append the disclosure to the greeting when it is not already there."
              checked={draft.compliance?.disclosure_required ?? true}
              onChange={(v) => setSection('compliance', { disclosure_required: v })}
              disabled={readOnly}
            />
          </div>

          <div className="mt-4 rounded-md bg-sunken p-3 text-xs text-ink-600 dark:text-ink-300">
            Negative intent is detected by deterministic phrase rules before any model call,
            so a clear &ldquo;stop calling me&rdquo; is honoured even if the AI provider is
            slow or unavailable. The model can add a refusal the rules missed, but it can
            never overturn one.
          </div>
        </Card>
      )}

      {tab === 'Voice' && (
        <Card
          title="Voice"
          description="How the bot sounds. Read from your speech provider, so a voice you have cloned appears here too."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Voice"
              hint={
                voicesList.length
                  ? 'Pick one, or paste an id below \u2014 a library voice, or one on another account.'
                  : 'No voice list available, so an id is accepted directly.'
              }
            >
              {voicesList.length ? (
                <Select
                  value={selectedVoice}
                  onChange={(e) => setSection('voice', { voice_id: e.target.value })}
                  disabled={readOnly}
                >
                  <option value="">Platform default</option>
                  {femaleVoices.length > 0 && (
                    <optgroup label="Female">
                      {femaleVoices.map((voice) => (
                        <option key={voice.voice_id} value={voice.voice_id}>
                          {describeVoice(voice)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {maleVoices.length > 0 && (
                    <optgroup label="Male">
                      {maleVoices.map((voice) => (
                        <option key={voice.voice_id} value={voice.voice_id}>
                          {describeVoice(voice)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {otherVoices.length > 0 && (
                    <optgroup label="Unlabelled">
                      {otherVoices.map((voice) => (
                        <option key={voice.voice_id} value={voice.voice_id}>
                          {describeVoice(voice)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {/* A voice configured before it was renamed or removed must
                      still show as set, or saving this form would silently
                      change the bot's voice. */}
                  {unlistedVoice && (
                    <option value={unlistedVoice}>{unlistedVoice} (not in the list)</option>
                  )}
                </Select>
              ) : (
                <Input
                  value={draft.voice?.voice_id ?? ''}
                  onChange={(e) => setSection('voice', { voice_id: e.target.value })}
                  placeholder={voicesPending ? 'Loading voices\u2026' : 'Provider voice id'}
                  disabled={readOnly}
                />
              )}
              {/* Always, not only when the list is empty. A provider that CAN
                  list voices lists the ones on this account — and the voice
                  somebody wants is often not among them: a voice from the
                  public library, or one on an account this platform's key does
                  not belong to. The list is a convenience; the id is the
                  setting, and both write the same field. */}
              {voicesList.length > 0 && (
                <Input
                  className="mt-2"
                  value={draft.voice?.voice_id ?? ''}
                  onChange={(e) => setSection('voice', { voice_id: e.target.value })}
                  placeholder="or paste a voice id"
                  autoComplete="off"
                  disabled={readOnly}
                />
              )}
            </Field>
            <Field label="Speed" hint="1 is the provider's natural pace.">
              <Input
                type="number"
                step="0.05"
                min={0.5}
                max={2}
                value={draft.voice?.speed ?? 1}
                onChange={(e) => setSection('voice', { speed: Number(e.target.value) })}
                disabled={readOnly}
              />
            </Field>
          </div>

          <div className="mt-4">
            <Toggle
              label="Barge-in"
              hint="Stop speaking the moment the customer starts talking. Leave this on: talking over someone is the clearest sign of a machine."
              checked={draft.voice?.barge_in ?? true}
              onChange={(v) => setSection('voice', { barge_in: v })}
              disabled={readOnly}
            />
          </div>

          {selectedVoice && !unlistedVoice && (
            <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
              Calls will log the voice they resolved as{' '}
              <code className="font-mono">voice_id</code>, so you can confirm the one
              you picked is the one that spoke.
            </p>
          )}

          {voices?.mock && (
            <div className="mt-4 rounded-md bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              No speech provider is configured, so this is the built-in test voice. Add a
              speech provider key on the platform Setup page to get real voices.
            </div>
          )}
          {!voices?.mock && voicesList.length === 0 && !voicesPending && (
            <div className="mt-4 rounded-md bg-sunken p-3 text-xs text-ink-600 dark:text-ink-300">
              The provider returned no voices. Check the speech provider key on the platform
              Setup page; meanwhile a voice id typed here is used as-is.
            </div>
          )}
        </Card>
      )}

      {tab === 'Advanced' && (
        <div className="space-y-4">
          <Card title="Prompt" description="Appended to the engine's own system prompt.">
            <Field label="Persona">
              <Input
                value={draft.prompt?.persona ?? ''}
                onChange={(e) => setSection('prompt', { persona: e.target.value })}
                disabled={readOnly}
              />
            </Field>
            <Field
              label="Extra rules"
              hint="One per line. Cannot override the hard compliance rules."
            >
              <Textarea
                rows={5}
                value={(draft.prompt?.rules ?? []).join('\n')}
                onChange={(e) =>
                  setSection('prompt', {
                    rules: e.target.value.split('\n').filter((s) => s.trim()),
                  })
                }
                disabled={readOnly}
              />
            </Field>
          </Card>
        </div>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

